import { useParams } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  HStack,
  Link,
  LinkBox,
  LinkOverlay,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Show,
  Skeleton,
  SkeletonText,
  Stack,
  Text,
  useColorMode,
  useDisclosure,
  useTheme,
  useToast,
  VStack
} from '@chakra-ui/react';
import { types, usePhoton } from '@photonhealth/react';
import { FiChevronRight } from 'react-icons/fi';
import { Page } from '../components/Page';
import PatientView from '../components/PatientView';
import { confirmWrapper } from '../components/GuardDialog';
import { formatAddress, formatDate, formatPhone, getMedicationNames } from '../../utils';
import OrderStatusBadge from '../components/OrderStatusBadge';
import InfoGrid from '../components/InfoGrid';
import CopyText from '../components/CopyText';
import { LocalPickup } from './NewOrder/components/SelectPharmacyCard/components/LocalPickup';
import { LocationResults, LocationSearch } from '../components/LocationSearch';
import SectionTitleRow from '../components/SectionTitleRow';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { CANCEL_ORDER, ROUTE_ORDER } from '../../mutations';
import { TicketModal } from '../components/TicketModal';
import { Fill, Order as OrderType } from '@photonhealth/sdk/dist/types';
import { datadogRum } from '@datadog/browser-rum';
import { useProviderAnalytics } from '../../hooks/useProviderAnalytics';
import { StyledToast } from '../components/StyledToast';
import { RerouteOrderButton } from '../components/RerouteOrderButton';
import {
  GET_ORDER,
  GET_ORDER_QUERY_NAME,
  getOrderRoutingHistory,
  PHARMACY_QUERY
} from '../../queries';
import { OrderRoutingHistory } from '../../gql/graphql';
import { OrderState } from 'packages/sdk/src/types';
import usePermissions from '../../hooks/usePermissions';
import { ResendOrderButton } from '../components/ResendOrderButton';
import NameView from '../components/NameView';

export const ORDER_FULFILLMENT_TYPE_MAP = {
  [types.FulfillmentType.PickUp]: 'Pick up',
  [types.FulfillmentType.MailOrder]: 'Mail order'
};

const cancelReasons = [
  'Wrong patient selected',
  'Wrong drug selected',
  'Wrong directions written',
  'Wrong pharmacy selected',
  'Therapy change',
  'Patient no longer taking',
  'Changing pharmacies',
  'Other'
];

function uniqueFills(order: OrderType): Fill[] {
  const treatmentNames = new Set();
  return order.fills.filter((fill) =>
    treatmentNames.has(fill.treatment.name) ? false : treatmentNames.add(fill.treatment.name)
  );
}

const rxSummary = (fill: Fill): string => {
  const dispenseQuantity = fill.prescription?.dispenseQuantity || '';
  const dispenseUnit = fill.prescription?.dispenseUnit || '';
  const fillsAllowed = fill.prescription?.fillsAllowed ? fill.prescription.fillsAllowed - 1 : '';
  const instructions = fill.prescription?.instructions || '';
  return `${dispenseQuantity} ${dispenseUnit}, ${fillsAllowed} Refills - ${instructions}`;
};

// ripped from components
function formatRxString({
  dispenseQuantity = 0,
  dispenseUnit = '',
  fillsAllowed = 0,
  instructions = ''
}: {
  dispenseQuantity?: number;
  dispenseUnit?: string;
  fillsAllowed?: number;
  instructions?: string;
}): string {
  const refills = Math.max(fillsAllowed - 1, 0);
  return `${dispenseQuantity} ${dispenseUnit}, ${refills} Refill${
    refills === 1 ? '' : 's'
  } - ${instructions}`;
}

function formatTicketContext({ order, fills }: { order: OrderType; fills: Fill[] }) {
  return `
Order:
  ID: ${order.id}

----
Patient:
  ID: ${order?.patient?.id}
  Name: ${order?.patient?.name?.full}

----
Prescriptions:
${fills.map(
  (fill) => `
  Name: ${fill.treatment.name}
  Info: ${formatRxString({
    dispenseQuantity: fill?.prescription?.dispenseQuantity,
    dispenseUnit: fill?.prescription?.dispenseUnit,
    fillsAllowed: fill?.prescription?.fillsAllowed,
    instructions: fill?.prescription?.instructions
  })}
`
)}

----
Description:
  `;
}

export const OrderDetailPage = () => {
  const params = useParams();
  const id = params.orderId!;
  const { user, getToken, clinicalClient } = usePhoton();
  const { track } = useProviderAnalytics();
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [updating, setUpdating] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [pharmacyId, setPharmacyId] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const cancelReasonRef = useRef(cancelReason);
  const client = useApolloClient();
  const theme = useTheme();
  const toast = useToast();

  const canRerouteOrder = usePermissions(['write:order', 'update:order', 'reroute:order'], {
    hasAny: true
  });

  const { data, loading, error } = useQuery(GET_ORDER, {
    variables: { id }
  });

  const routingHistoryRes = useQuery(getOrderRoutingHistory, {
    client: clinicalClient,
    variables: { id }
  });
  const routingHistory = routingHistoryRes.data?.order?.routingHistory ?? [];

  useEffect(() => {
    cancelReasonRef.current = cancelReason;
  }, [cancelReason]);

  const [cancelOrder] = useMutation(CANCEL_ORDER, {
    onQueryUpdated: (observableQuery) => {
      if (observableQuery.queryName !== GET_ORDER_QUERY_NAME) {
        return client?.defaultOptions?.mutate?.onQueryUpdated;
      }
    }
  });
  const [routeOrder] = useMutation(ROUTE_ORDER, {
    update: async (cache) => {
      // after routing an order, we need to update the cache with the new pharmacy data optimistically
      const existingOrder: { order: types.Order } | null = cache.readQuery({
        query: GET_ORDER,
        variables: { id: id! }
      });
      try {
        const { data: pharmacyData } = await client.query({
          query: PHARMACY_QUERY,
          variables: { id: pharmacyId }
        });
        if (existingOrder && existingOrder.order && pharmacyData) {
          cache.writeQuery({
            query: GET_ORDER,
            data: {
              order: {
                ...existingOrder.order,
                state: types.OrderState.Placed,
                pharmacy: pharmacyData.pharmacy
              }
            },
            variables: { id: id! }
          });
        }
      } catch (error) {
        console.error('Error fetching pharmacy data:', error);
      }
    }
  });

  const order = data?.order;
  const {
    isOpen: isOpenLocation,
    onOpen: onOpenLocation,
    onClose: onCloseLocation
  } = useDisclosure();
  const [location, setLocation] = useState<LocationResults>({
    lat: undefined,
    lng: undefined,
    loc: ''
  });
  const geocoder = new google.maps.Geocoder();
  const getAccessToken = async () => {
    try {
      const token = await getToken();
      setAccessToken(token);
    } catch (e) {
      console.error(e);
    }
  };
  useEffect(() => {
    if (!accessToken) {
      getAccessToken();
    }
  }, [accessToken]);

  useEffect(() => {
    // Scroll to top on initial load
    document?.getElementById('root')?.querySelector('section')?.scrollTo(0, 0);
  }, []);

  const { colorMode } = useColorMode();

  const prescriptions = useMemo(() => {
    if (!order) return [];

    const rxIds = new Set();
    return order.fills.filter((fill: any) => {
      if (rxIds.has(fill.prescription.id)) return false;
      rxIds.add(fill.prescription.id);
      return true;
    });
  }, [order]);

  useEffect(() => {
    const address = order?.patient?.address;
    if (!address) return;
    geocoder
      .geocode({ address: formatAddress(address) })
      .then(({ results }) => {
        if (results) {
          setLocation({
            loc: results[0].formatted_address,
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng()
          });
        }
      })
      .catch((err) => {
        console.log('Error geocoding', err);
      });
  }, [order?.patient?.address]);

  if (error || (!loading && !order)) {
    return (
      <Alert
        status="warning"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        height="200px"
      >
        <AlertIcon />
        <AlertTitle mt={4} mb={1} fontSize="lg">
          Unknown Order
        </AlertTitle>
        <AlertDescription maxWidth="sm">
          <div>Looks like we can't find an order with that ID. </div>
          <Link textDecoration="underline" fontSize="md" href="/orders">
            Go back to orders.
          </Link>
        </AlertDescription>
      </Alert>
    );
  }

  const fills = order ? uniqueFills(order) : [];
  const medicationNames = getMedicationNames(fills);
  const displayPharmacy = order?.pharmacy || order?.patient.preferredPharmacies[0];

  return (
    <>
      <TicketModal
        isOpen={isTicketModalOpen}
        onClose={() => setIsTicketModalOpen(false)}
        subject={`Issue with order for ${order?.patient?.name?.full}`}
        body={
          <>
            <Text>Start an email thread with the Photon team to discuss next steps.</Text>
            <Card
              p={4}
              bg={theme.colors.navy['50']}
              borderWidth="1px"
              borderColor={theme.colors.navy['200']}
              borderRadius="lg"
              variant="outline"
            >
              <VStack spacing={4}>
                <Box w="100%">
                  <Text fontSize="xs" color={theme.colors.navy['700']}>
                    PATIENT
                  </Text>
                  <Text fontSize="sm" fontWeight="medium">
                    {order?.patient && order.patient.name.full}
                  </Text>
                </Box>
                <VStack w="100%" align="left">
                  <Text fontSize="xs" color={theme.colors.navy['700']}>
                    PRESCRIPTION
                  </Text>
                  {fills?.length > 0 &&
                    fills.map((fill) => (
                      <Box w="100%" key={fill.treatment.name}>
                        <Text fontSize="sm" fontWeight="medium">
                          {fill?.treatment?.name}
                        </Text>
                        <Text fontSize="sm" color={theme.colors.navy['500']}>
                          {formatRxString({
                            dispenseQuantity: fill?.prescription?.dispenseQuantity,
                            dispenseUnit: fill?.prescription?.dispenseUnit,
                            fillsAllowed: fill?.prescription?.fillsAllowed,
                            instructions: fill?.prescription?.instructions
                          })}
                        </Text>
                      </Box>
                    ))}
                </VStack>
              </VStack>
            </Card>
          </>
        }
        prependContext={!order ? '' : formatTicketContext({ order, fills })}
      />
      <Page
        kicker="ORDER"
        header={
          loading ? (
            <SkeletonText skeletonHeight={5} noOfLines={1} width="300px" mt={2} />
          ) : (
            <CopyText text={id || ''} />
          )
        }
        buttons={
          <Stack
            direction={{ base: 'column-reverse', md: 'row' }}
            w={{ base: 'full', sm: undefined }}
            justify="end"
          >
            <Button
              aria-label="Cancel Order"
              variant="outline"
              borderColor="red.500"
              textColor="red.500"
              colorScheme="red"
              isLoading={updating}
              loadingText="Canceling..."
              isDisabled={
                loading ||
                order?.state === types.OrderState.Canceled ||
                order?.state === types.OrderState.Completed ||
                order?.fulfillment?.state === 'SHIPPED'
              }
              onClick={async () => {
                datadogRum.addAction('cancel_order_btn_click', {
                  orderId: id
                });

                const decision = await confirmWrapper('Cancel this order?', {
                  description: (
                    <RadioGroup onChange={setCancelReason}>
                      <Text mb={2}>Please select a reason for canceling</Text>
                      <Stack direction="column">
                        {cancelReasons.map((reason) => (
                          <Radio key={reason} value={reason}>
                            {reason}
                          </Radio>
                        ))}
                      </Stack>
                    </RadioGroup>
                  ),
                  cancelText: "No, Don't Cancel",
                  confirmText: 'Yes, Cancel',
                  darkMode: colorMode !== 'light',
                  colorScheme: 'red'
                });
                if (decision) {
                  setUpdating(true);
                  track('Order Canceled', {
                    buttonText: 'Cancel Order',
                    orderId: id
                  });
                  try {
                    const variables = {
                      id,
                      ...(cancelReasonRef.current && { reason: cancelReasonRef.current })
                    };
                    await cancelOrder({ variables });
                  } catch (error) {
                    toast({
                      position: 'top-right',
                      duration: 4000,
                      render: ({ onClose }) => (
                        <StyledToast
                          onClose={onClose}
                          type="error"
                          title="Error Canceling Order"
                          description={`There was an error canceling the order: ${error}`}
                        />
                      )
                    });
                  } finally {
                    setUpdating(false);
                  }
                }
              }}
            >
              Cancel Order
            </Button>
            <Button
              aria-label="Report Issue"
              colorScheme="blue"
              onClick={() => {
                datadogRum.addAction('report_issue_btn_click', {
                  orderId: id
                });

                setIsTicketModalOpen(true);
              }}
              isDisabled={loading}
            >
              Report Issue
            </Button>
          </Stack>
        }
      >
        <Card>
          <CardHeader>
            <Stack
              direction={{ base: 'column', md: 'row' }}
              justify="space-between"
              align="start"
              width="full"
              spacing={4}
            >
              <VStack w="full" align="start">
                {loading ? (
                  <SkeletonText skeletonHeight={5} noOfLines={1} w="300px" />
                ) : (
                  medicationNames.map((med, i: number) => (
                    <Text key={i} fontWeight="medium" flex="1">
                      {med}
                    </Text>
                  ))
                )}
              </VStack>
              {loading ? (
                <Skeleton width="70px" height="24px" borderRadius="xl" />
              ) : (
                <OrderStatusBadge
                  fulfillmentState={order?.fulfillment?.state}
                  orderState={order?.state}
                />
              )}
            </Stack>
          </CardHeader>
          <Divider color="gray.100" />
          <CardBody>
            <Stack direction={{ base: 'column', sm: 'row' }} gap={[5, 3]} w="full">
              <VStack align="start">
                <Text color="gray.500" fontWeight="medium" fontSize="sm">
                  Patient
                </Text>
                {loading ? (
                  <HStack alignContent="center" w="150px" display="flex">
                    <SkeletonText skeletonHeight={5} noOfLines={2} flexGrow={1} />
                  </HStack>
                ) : (
                  <PatientView patient={order?.patient} />
                )}
              </VStack>
              <Show above="sm">
                <Divider orientation="vertical" height="auto" />
              </Show>
              <VStack align="start">
                <Text color="gray.500" fontWeight="medium" fontSize="sm">
                  Created At
                </Text>
                {loading ? (
                  <SkeletonText skeletonHeight={5} noOfLines={1} width="125px" />
                ) : (
                  <Text fontSize="md">{formatDate(order?.createdAt)}</Text>
                )}
              </VStack>
              {order?.supervisor && (
                <>
                  <Show above="sm">
                    <Divider orientation="vertical" height="auto" />
                  </Show>
                  <VStack align="start">
                    <Text color="gray.500" fontWeight="medium" fontSize="sm">
                      Supervisor
                    </Text>
                    {loading ? (
                      <SkeletonText skeletonHeight={5} noOfLines={1} width="125px" />
                    ) : (
                      <NameView
                        name={`${order.supervisor.firstName} ${order.supervisor.lastName}`}
                        sub={`NPI: ${order.supervisor.npi}`}
                      />
                    )}
                  </VStack>
                </>
              )}
              {order?.externalId ? (
                <>
                  <Show above="sm">
                    <Divider orientation="vertical" height="auto" />
                  </Show>
                  <VStack align="start">
                    <Text color="gray.500" fontWeight="medium" fontSize="sm">
                      External Id
                    </Text>

                    <CopyText text={order?.externalId} size="xs" />
                  </VStack>
                </>
              ) : null}
            </Stack>
          </CardBody>
          <Divider color="gray.100" />
          <CardBody>
            <VStack
              spacing={4}
              fontSize={{ base: 'md', md: 'lg' }}
              alignItems="start"
              w="100%"
              mt={0}
            >
              <SectionTitleRow
                headerText="Pharmacy Information"
                rightElement={
                  canRerouteOrder && order && order?.state !== 'ROUTING' ? (
                    <HStack justify="right" spacing="2">
                      {order.pharmacy && <ResendOrderButton order={order} />}
                      <RerouteOrderButton organizationId={user.org_id} order={order} />
                    </HStack>
                  ) : undefined
                }
              />

              {order?.state === types.OrderState.Routing ? (
                <>
                  <Card backgroundColor="gray.50" shadow="none" variant="outline">
                    <CardBody p={3}>
                      <VStack spacing={1} align="start">
                        <VStack spacing={0} align="start">
                          <Text fontSize="md" fontWeight="medium">
                            {getRoutingOrderStatusText(order, routingHistory)}
                          </Text>
                          <Text fontSize="md" color="gray.500">
                            {getRoutingOrderSubText(order, routingHistory)}
                          </Text>
                        </VStack>
                        {shouldShowSelectPharmacyButton(order, routingHistory) ? (
                          <Button
                            onClick={() => {
                              datadogRum.addAction('select_pharmacy_btn_click', {
                                orderId: id
                              });

                              onOpen();
                            }}
                            colorScheme="blue"
                            variant="link"
                            mt={2}
                          >
                            Select Pharmacy
                          </Button>
                        ) : null}
                      </VStack>
                    </CardBody>
                  </Card>
                  <LocationSearch
                    isOpen={isOpenLocation}
                    onClose={({ loc, lat, lng }) => {
                      if (loc && lat && lng) {
                        setLocation({ loc, lat, lng });
                      }
                      onCloseLocation();
                    }}
                  />
                  <Modal isOpen={isOpen} onClose={onClose}>
                    <ModalOverlay />
                    <ModalContent>
                      <ModalHeader>Select a Pharmacy</ModalHeader>
                      <ModalCloseButton />

                      <ModalBody>
                        <LocalPickup
                          location={location.loc}
                          latitude={location.lat}
                          longitude={location.lng}
                          onOpen={onOpenLocation}
                          patient={order.patient}
                          pharmacyId=""
                          updatePreferredPharmacy={false}
                          setUpdatePreferredPharmacy={() => {}}
                          preferredPharmacyIds={[]}
                          setFieldValue={(_, id) => {
                            setPharmacyId(id);
                          }}
                          resetSelection={() => {}}
                        />
                      </ModalBody>

                      <ModalFooter>
                        <Button
                          aria-label="Close Pharmacy Select Modal"
                          variant="solid"
                          size="sm"
                          mr={3}
                          onClick={() => {
                            setPharmacyId('');
                            onClose();
                          }}
                        >
                          Close
                        </Button>
                        <Button
                          aria-label="Set Pharmacy"
                          variant="solid"
                          colorScheme="blue"
                          size="sm"
                          isLoading={updating}
                          loadingText="Setting Pharmacy..."
                          isDisabled={!pharmacyId}
                          onClick={async () => {
                            setUpdating(true);
                            try {
                              track('Pharmacy Selected by Provider', {
                                orderId: id,
                                pharmacyId
                              });
                              await routeOrder({ variables: { id, pharmacyId } });
                              setPharmacyId('');
                              onClose();
                            } catch (error) {
                              const message = error instanceof Error ? error.message : `${error}`;
                              toast({
                                position: 'top-right',
                                duration: 4000,
                                render: ({ onClose: onToastClose }) => (
                                  <StyledToast
                                    onClose={onToastClose}
                                    type="error"
                                    title="Error Setting Pharmacy"
                                    description={`There was an error setting the pharmacy: ${message}`}
                                  />
                                )
                              });
                            } finally {
                              setUpdating(false);
                            }
                          }}
                        >
                          Set Pharmacy
                        </Button>
                      </ModalFooter>
                    </ModalContent>
                  </Modal>
                </>
              ) : null}

              {order &&
                displayPharmacy &&
                !isRequestingRerouteFromPatient(order, routingHistory) && (
                  <>
                    <InfoGrid name="Name">
                      {loading ? (
                        <SkeletonText skeletonHeight={5} noOfLines={1} width="100px" />
                      ) : displayPharmacy?.name ? (
                        <Text fontSize="md">{displayPharmacy.name}</Text>
                      ) : (
                        <Text fontSize="md" as="i" color="gray.500">
                          None
                        </Text>
                      )}
                    </InfoGrid>

                    <InfoGrid name="Phone">
                      {loading ? (
                        <SkeletonText skeletonHeight={5} noOfLines={1} width="100px" />
                      ) : displayPharmacy?.phone ? (
                        <Link fontSize="md" href={`tel:${displayPharmacy.phone}`} isExternal>
                          {formatPhone(displayPharmacy.phone)}
                        </Link>
                      ) : (
                        <Text fontSize="md" as="i" color="gray.500">
                          None
                        </Text>
                      )}
                    </InfoGrid>

                    <InfoGrid name="Address">
                      {loading ? (
                        <SkeletonText skeletonHeight={5} noOfLines={1} width="100px" />
                      ) : displayPharmacy?.address ? (
                        <Text fontSize="md">{formatAddress(displayPharmacy.address)}</Text>
                      ) : (
                        <Text fontSize="md" as="i" color="gray.500">
                          None
                        </Text>
                      )}
                    </InfoGrid>

                    <InfoGrid name="Id">
                      {loading ? (
                        <SkeletonText skeletonHeight={5} noOfLines={1} width="100px" />
                      ) : displayPharmacy?.id ? (
                        <CopyText text={displayPharmacy.id} />
                      ) : (
                        <Text fontSize="md" as="i" color="gray.500">
                          None
                        </Text>
                      )}
                    </InfoGrid>
                  </>
                )}

              {!loading && order?.fulfillment?.type === 'MAIL_ORDER' ? (
                <>
                  <InfoGrid name="Delivery Address">
                    {order?.address ? (
                      <Text fontSize="md">{formatAddress(order.address)}</Text>
                    ) : (
                      <Text fontSize="md" as="i" color="gray.500">
                        None
                      </Text>
                    )}
                  </InfoGrid>
                  <InfoGrid name="Carrier">
                    {order.fulfillment?.carrier ? (
                      <Text fontSize="md">{order.fulfillment.carrier}</Text>
                    ) : (
                      <Text fontSize="md" as="i" color="gray.500">
                        None
                      </Text>
                    )}
                  </InfoGrid>
                  <InfoGrid name="Tracking Number">
                    {order.fulfillment?.trackingNumber ? (
                      <Text fontSize="md">{order.fulfillment.trackingNumber}</Text>
                    ) : (
                      <Text fontSize="md" as="i" color="gray.500">
                        None
                      </Text>
                    )}
                  </InfoGrid>
                </>
              ) : null}

              <SectionTitleRow headerText="Prescription Fills" />

              {loading ? (
                <SkeletonText skeletonHeight={20} noOfLines={1} width="300px" />
              ) : prescriptions.length === 0 ? (
                <Text as="i" fontSize="sm" color="gray.500">
                  No fills
                </Text>
              ) : (
                <VStack spacing={3}>
                  {prescriptions.map((fill: Fill) => (
                    <LinkBox key={fill.id} style={{ textDecoration: 'none' }} w="full">
                      <Card
                        variant="outline"
                        p={3}
                        shadow="none"
                        backgroundColor="gray.50"
                        _hover={{ backgroundColor: 'gray.100' }}
                      >
                        <HStack justify="space-between">
                          <VStack alignItems="start" spacing={0}>
                            <LinkOverlay href={`/prescriptions/${fill?.prescription?.id}`}>
                              <Text fontSize="md">{fill.treatment.name}</Text>
                            </LinkOverlay>
                            <Text fontSize="md" color="gray.500">
                              {rxSummary(fill)}
                            </Text>
                          </VStack>
                          <Box alignItems="end">
                            <FiChevronRight size="1.3em" />
                          </Box>
                        </HStack>
                      </Card>
                    </LinkBox>
                  ))}
                </VStack>
              )}
            </VStack>
          </CardBody>
        </Card>
      </Page>
    </>
  );
};

function getRoutingOrderStatusText(order: OrderType, routingHistory: OrderRoutingHistory[]) {
  switch (true) {
    case inPreferredPharmacyRoutingWindow(order, routingHistory):
      return 'This order is pending pharmacy confirmation by the patient.';
    case isRequestingRerouteFromPatient(order, routingHistory):
      return 'This order is pending a pharmacy reroute selection from the patient.';
    default: // initial STP flow, never had an initial pharmacy selection
      return 'This order is pending pharmacy selection by the patient.';
  }
}

function getRoutingOrderSubText(order: OrderType, routingHistory: OrderRoutingHistory[]) {
  switch (true) {
    case inPreferredPharmacyRoutingWindow(order, routingHistory):
      return 'The order will be routed to the below pharmacy if the patient does not confirm within 15 minutes.';
    default:
      return 'Select a pharmacy for the patient if needed.';
  }
}

function shouldShowSelectPharmacyButton(
  order: OrderType,
  routingHistory: OrderRoutingHistory[]
): boolean {
  return !order.pharmacy || isRequestingRerouteFromPatient(order, routingHistory);
}

function inPreferredPharmacyRoutingWindow(order: OrderType, routingHistory: OrderRoutingHistory[]) {
  const isRouting = order.state === OrderState.Routing;
  const hasPreferredPharmacy = order.patient.preferredPharmacies?.length;
  const isInitialRoute = !routingHistory.length;

  return isRouting && hasPreferredPharmacy && isInitialRoute;
}

function isRequestingRerouteFromPatient(order: OrderType, routingHistory: OrderRoutingHistory[]) {
  const isRouting = order.state === OrderState.Routing;
  const hasPharmacy = !!order.pharmacy;
  const isInitialRoute = !routingHistory.length;

  return isRouting && hasPharmacy && !isInitialRoute;
}
